import { useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { dashboardService } from '@/services/api';
import { useAuthentication } from '@/common-hooks/use-authentication';
import type { DashboardData } from '@/common-types';
import type { DashboardViewType } from '@/types/application/common/context/portal-context-model';
import { DashboardContext } from './dashboard-context';

export function DashboardProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, loading: authLoading } = useAuthentication();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewType, setViewType] = useState<DashboardViewType>('customers');

  const refresh = useCallback(async (days = 30) => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getDashboardUrls();
      const urlData = response.data;
      const timeRange = days === 7 ? 'dashboard7d' : 'dashboard30d';
      const viewData = viewType === 'customers' ? urlData.customers : urlData.all;
      const url = viewData[timeRange].url;
      const dashboardData = await dashboardService.getDashboardData(url);
      setData(dashboardData);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, viewType]);

  const lastTimeRangeRef = useRef<number>(30);
  const dashboardFetchingRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated && user) {
      if (dashboardFetchingRef.current) return;
      dashboardFetchingRef.current = true;
      refresh(lastTimeRangeRef.current).finally(() => {
        dashboardFetchingRef.current = false;
      });
    } else {
      setLoading(false);
      setData(null);
    }
  }, [isAuthenticated, user, authLoading]);

  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      if (dashboardFetchingRef.current) return;
      dashboardFetchingRef.current = true;
      refresh(lastTimeRangeRef.current).finally(() => {
        dashboardFetchingRef.current = false;
      });
    }
  }, [viewType]);

  const refreshWithTimeRange = useCallback(async (days = 30) => {
    lastTimeRangeRef.current = days;
    await refresh(days);
  }, [refresh]);

  return (
    <DashboardContext.Provider value={{ data, loading, error, viewType, setViewType, refresh: refreshWithTimeRange }}>
      {children}
    </DashboardContext.Provider>
  );
}
